import React from 'react';
import { Button } from '@roger/ui-components';
import { AlertCircle } from 'lucide-react';

interface CarryoverStagedBannerProps {
  /** How many answers were copied from the source visit. */
  count: number;
  /** Human-readable source visit, e.g. "SN Routine — Mar 5, 2026". */
  sourceLabel: string | null;
  onKeep: () => void;
  onDiscard: () => void;
}

/**
 * Staged-answers bar for visit answer carryover.
 *
 * This is the clinician's only signal that the copied answers are not saved
 * yet, so the caller keeps it visible until they keep or discard them.
 */
export const CarryoverStagedBanner: React.FC<CarryoverStagedBannerProps> = ({
  count,
  sourceLabel,
  onKeep,
  onDiscard,
}) => (
  // Matches the Signature Notice banner in VisitDetailPanel so the two
  // read as one system.
  <div className='mb-3 rounded-lg border border-amber-200 bg-amber-50 p-4'>
    <div className='flex items-start gap-3'>
      <AlertCircle className='mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600' />
      <div className='flex flex-1 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h4 className='mb-1 font-semibold text-amber-900'>
            Copied answers not saved yet
          </h4>
          <p className='text-sm text-amber-800'>
            {count} answer{count === 1 ? '' : 's'} copied
            {sourceLabel ? ` from ${sourceLabel}` : ''}. Any edits made now are
            not saved until &quot;Keep&quot; is selected.
          </p>
        </div>
        <div className='flex shrink-0 gap-2'>
          <Button variant='outline' size='sm' onClick={onDiscard}>
            Discard
          </Button>
          <Button size='sm' onClick={onKeep}>
            Keep
          </Button>
        </div>
      </div>
    </div>
  </div>
);
